// /app/frontend/src/pages/VerifyAgeExplainerPage.jsx
/**
 * Age Verification Explainer Page
 * ================================
 * Pre-Stripe-Identity gateway that explains WHY verification exists,
 * what information is and isn't stored, and the four-step process.
 *
 * Flow: /verify-age (this page) → "Continue with Verification" →
 *       /verify-age/start (Stripe Identity redirect).
 *
 * Already-verified users see a "verification complete" state with no CTA.
 *
 * Routes:
 *   /verify-age              -> this page (explainer)
 *   /verify-age/start        -> existing VerifyAgePage.js (Stripe redirect)
 *
 * The explainer preserves the ?return= query param so the post-Stripe
 * redirect still lands the user back where they came from.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  ArrowLeft,
  ArrowRight,
  ShieldOff,
  Bot,
  Lock,
  Users,
  CheckCircle2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { fetchAgeVerificationStatus } from '../api/ageVerification';
import { getVerifyReturnUrl } from '../utils/verificationNav';

const STRIPE_PRIVACY_URL = 'https://stripe.com/privacy';
const UNITYLOVE_PRIVACY_URL = '/privacy';

export default function VerifyAgeExplainerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  const returnUrl = getVerifyReturnUrl(searchParams);
  const returnQuery = returnUrl
    ? `?return=${encodeURIComponent(returnUrl)}`
    : '';

  const loadStatus = useCallback(async () => {
    try {
      const data = await fetchAgeVerificationStatus();
      setIsVerified(Boolean(data?.is_age_verified));
    } catch {
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleContinue = () => {
    navigate(`/verify-age/start${returnQuery}`);
  };

  const handleMaybeLater = () => {
    navigate(returnUrl || '/dashboard');
  };

  const handleBack = () => {
    navigate(returnUrl || -1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50">
        <div className="flex items-center justify-center py-24">
          <Loader2
            className="h-8 w-8 animate-spin text-rose-500"
            data-testid="verify-explainer-loading"
          />
        </div>
      </div>
    );
  }

  // ---------------- ALREADY VERIFIED STATE ----------------
  if (isVerified) {
    return (
      <div
        className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50"
        data-testid="verify-explainer-already-verified"
      >
        <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
          <button
            type="button"
            onClick={handleBack}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 transition"
            data-testid="verify-explainer-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>

          <div className="rounded-3xl border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-900/20 p-8 sm:p-10 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white">
              <ShieldCheck className="h-8 w-8" />
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white">
              You&rsquo;re Already Verified
            </h1>
            <p className="mt-3 text-slate-700 dark:text-slate-300">
              Your account has already completed identity verification. You have full
              access to UnityLove&rsquo;s trusted community features.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <ShieldCheck className="h-4 w-4" />
              Verified Member
            </div>
            <div className="mt-8">
              <button
                type="button"
                onClick={() => navigate(returnUrl || '/dashboard')}
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-6 py-3 text-sm font-semibold text-white hover:bg-rose-700 active:scale-95 transition"
                data-testid="verify-explainer-continue-app-btn"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- EXPLAINER (UNVERIFIED) ----------------
  return (
    <div
      className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50"
      data-testid="verify-explainer-page"
    >
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        {/* Top bar */}
        <button
          type="button"
          onClick={handleBack}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 transition"
          data-testid="verify-explainer-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        {/* HERO */}
        <section className="text-center mb-10">
          <div className="mx-auto mb-5 inline-flex items-center justify-center h-14 w-14 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h1
            className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
            data-testid="verify-explainer-headline"
          >
            Real People. Real Connections.
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed text-slate-700 dark:text-slate-300">
            UnityLove uses identity verification to help protect our community from
            fake accounts, catfishing, bots, impersonation, and underage users.
            Verification helps create a safer experience for everyone.
          </p>

          {/* Trust indicators */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {[
              'Real members',
              'Verified age',
              'Reduced catfishing',
              'Reduced bots',
            ].map((label) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {label}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rose-600 px-6 py-3 text-sm sm:text-base font-semibold text-white hover:bg-rose-700 active:scale-95 transition shadow-sm"
              data-testid="verify-explainer-primary-cta"
            >
              <ShieldCheck className="h-4 w-4" />
              Verify My Account
            </button>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-1 rounded-full px-5 py-3 text-sm font-medium text-slate-700 hover:text-rose-600 dark:text-slate-300 dark:hover:text-rose-300 transition"
              data-testid="verify-explainer-learn-more"
            >
              Learn How Verification Works
            </a>
          </div>
        </section>

        {/* TRUST PILLARS */}
        <section
          className="mb-10 grid gap-4 sm:grid-cols-2"
          data-testid="verify-explainer-pillars"
        >
          <PillarCard
            icon={ShieldOff}
            iconClass="text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/15"
            title="Anti-Catfish"
            body="Every verification helps ensure members are who they claim to be, reducing fake profiles and stolen photos."
          />
          <PillarCard
            icon={Bot}
            iconClass="text-violet-600 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15"
            title="Anti-Bot"
            body="Automated accounts and spam networks cannot easily pass identity verification requirements."
          />
          <PillarCard
            icon={Lock}
            iconClass="text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15"
            title="Privacy Matters"
            body="UnityLove does not store your ID documents on our servers. Verification is processed through Stripe Identity, a trusted identity verification platform used by major businesses worldwide."
          />
          <PillarCard
            icon={Users}
            iconClass="text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15"
            title="Community Protection"
            body="Verification helps create a more authentic environment where members can build meaningful connections with greater confidence."
          />
        </section>

        {/* HOW IT WORKS */}
        <section
          id="how-it-works"
          className="mb-10 scroll-mt-8"
          data-testid="verify-explainer-how-it-works"
        >
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-1">
            How It Works
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
            The whole process usually takes only a few minutes.
          </p>

          <ol className="space-y-3">
            <StepRow n={1} title="Take a photo of your government-issued ID." />
            <StepRow n={2} title="Take a quick selfie." />
            <StepRow n={3} title="Stripe Identity securely verifies the match." />
            <StepRow
              n={4}
              title="Your UnityLove profile receives a verified status."
            />
          </ol>
        </section>

        {/* PRIVACY */}
        <section
          className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60 p-5 sm:p-6"
          data-testid="verify-explainer-privacy"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 shrink-0">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white">
                Your Privacy Matters
              </h2>
              <p className="mt-2 text-sm sm:text-base text-slate-700 dark:text-slate-300">
                UnityLove uses Stripe Identity to perform age and identity
                verification. UnityLove does not store copies of your identification
                documents on our servers. Verification information is used only to
                help confirm authenticity, eligibility, and community safety
                requirements.
              </p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                <a
                  href={STRIPE_PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-rose-600 hover:text-rose-700 dark:text-rose-300 dark:hover:text-rose-200 underline-offset-4 hover:underline"
                  data-testid="verify-explainer-stripe-privacy-link"
                >
                  Stripe Identity Privacy Information
                </a>
                <a
                  href={UNITYLOVE_PRIVACY_URL}
                  className="text-rose-600 hover:text-rose-700 dark:text-rose-300 dark:hover:text-rose-200 underline-offset-4 hover:underline"
                  data-testid="verify-explainer-ul-privacy-link"
                >
                  UnityLove Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="mb-10" data-testid="verify-explainer-benefits">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-3">
            Why Members Choose Verification
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
            Verification helps unlock trusted community features across UnityLove.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Build trust faster',
              'Stand out as a verified member',
              'Help reduce fake accounts',
              'Participate in trusted community features',
              'Support a safer environment for everyone',
            ].map((benefit) => (
              <li
                key={benefit}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500 dark:text-emerald-400" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FINAL CTA */}
        <section
          className="rounded-3xl bg-gradient-to-br from-rose-50 to-amber-50 dark:from-rose-500/10 dark:to-amber-500/10 border border-rose-100 dark:border-rose-500/20 p-6 sm:p-8 text-center"
          data-testid="verify-explainer-final-cta"
        >
          <div className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-rose-600 text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
            Ready to Verify?
          </h2>
          <p className="mt-3 max-w-xl mx-auto text-slate-700 dark:text-slate-300 text-sm sm:text-base">
            The process usually takes only a few minutes and helps keep UnityLove
            authentic, safe, and community-focused. Verification is securely handled
            through Stripe Identity.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleContinue}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-rose-600 px-6 py-3 text-sm sm:text-base font-semibold text-white hover:bg-rose-700 active:scale-95 transition shadow-sm"
              data-testid="verify-explainer-final-continue-btn"
            >
              Continue with Verification
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleMaybeLater}
              className="inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium text-slate-700 hover:text-rose-600 dark:text-slate-300 dark:hover:text-rose-300 transition"
              data-testid="verify-explainer-maybe-later-btn"
            >
              Maybe Later
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function PillarCard({ icon: Icon, iconClass, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900/60">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full ${iconClass}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
        {title}
      </h3>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
        {body}
      </p>
    </div>
  );
}

function StepRow({ n, title }) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white text-sm font-semibold">
        {n}
      </div>
      <p className="text-sm sm:text-base text-slate-800 dark:text-slate-200 pt-1">
        {title}
      </p>
    </li>
  );
}
